import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { EditCommand, Edl } from '@repo/editor-core';
import { type AgentEvent, type ChatMessage, type ChatTurn, MAX_CHAT_TURNS } from '@/lib/agent-chat';

/** SSE chat transport, injected by the connected editor page. */
export interface AgentChatApi {
  send: (
    messages: ChatTurn[],
    edl: Edl,
    onEvent: (ev: AgentEvent) => void,
    signal: AbortSignal
  ) => Promise<void>;
  /**
   * Transcript saved on the server, restored when the editor opens. The panel
   * reads it once (on mount) — the server owns every write from then on.
   */
  history?: ChatMessage[];
  /** Drop the saved transcript ("새 대화"). */
  clear?: () => Promise<void>;
}

/** Display transcript item (a superset of what is sent back as ChatTurn). */
type Item =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'assistant'; text: string }
  | { id: string; kind: 'thinking'; text: string; live: boolean; seconds: number | null }
  | { id: string; kind: 'tool'; icon: string; name: string; meta: string; running: boolean }
  | { id: string; kind: 'notice'; text: string };

/**
 * Starter prompts — clicking fills the composer so the user can tweak & send.
 *
 * 칩은 **AI가 실제로 할 수 있는 일**만 제안해야 한다. AI가 담당하는 것은 컷·프레이밍·
 * 타이틀·GIF 뿐이다 — 자막은 전사에서 기계가 만들고, 효과음은 비활성화 상태다
 * (docs/shorts-editor-quality-redesign.md §5 P0-2/P0-3). 그 둘을 요청하는 칩을 두면
 * 사용자가 누르는 순간 거절당한다.
 */
const SAMPLE_REQUESTS: { label: string; prompt: string }[] = [
  {
    label: '핵심만 15초',
    prompt: '제일 임팩트 있는 순간만 남기고 15초 한 컷으로 잘라줘.',
  },
  {
    label: '30초로 정리',
    prompt: '늘어지는 부분 빼고 30초로 정리해줘. 말이 끊기지 않게 문장 단위로 잘라줘.',
  },
  {
    label: '화면 정리',
    prompt: '채팅창이랑 방송 오버레이 안 보이게 크롭하고, 영상이 화면을 꽉 채우게 해줘.',
  },
  {
    label: '제목 달기',
    prompt: '영상에서 실제로 한 말을 살려서 짧은 제목을 달아줘. 낚시성 문구는 빼고.',
  },
  {
    label: '밈/짤 GIF',
    prompt: '웃긴 순간에 어울리는 GIF 스티커를 하나만 얹어줘. (얼굴/자막은 안 가리게)',
  },
];

/** Rebuild transcript items from the turns the server kept. */
function restoreItems(history: ChatMessage[], nextId: () => string): Item[] {
  const out: Item[] = [];
  for (const m of history) {
    if (m.role === 'user') {
      out.push({ id: nextId(), kind: 'user', text: m.content });
      continue;
    }
    out.push({ id: nextId(), kind: 'assistant', text: m.content });
    if (m.edits && m.edits > 0) {
      out.push({
        id: nextId(),
        kind: 'tool',
        icon: '✂️',
        name: '타임라인 편집',
        meta: `${m.edits}개 적용`,
        running: false,
      });
    }
  }
  return out;
}

const Avatar = () => (
  <div
    className="flex h-6 w-6 flex-none place-items-center items-center justify-center rounded-full text-[0.7rem] font-bold text-[#160b06]"
    style={{
      background: 'radial-gradient(120% 120% at 30% 25%, hsl(var(--ai)/0.95), hsl(var(--ai)/0.45))',
    }}
    aria-hidden
  >
    ✦
  </div>
);

/**
 * Conversational AI editor panel — a live, Claude-style chat. The user talks; the
 * agent thinks, calls tools (edit / gif search) in a loop, and streams events that
 * this panel renders (thinking blocks, tool cards, messages) while replaying each
 * edit through the editor's own command bus via `onEdit`.
 */
export function AgentPanel({
  chat,
  getEdl,
  onEdit,
  onBusyChange,
  onTurnDone,
  onError,
}: {
  chat: AgentChatApi;
  /** Latest (possibly unsaved) timeline — sent so the agent reasons over what the user sees. */
  getEdl: () => Edl;
  onEdit: (command: EditCommand) => void;
  onBusyChange: (busy: boolean) => void;
  /**
   * 턴 종료. `persisted` 는 **서버가 최종 타임라인을 저장했는가** 다 —
   * 서버는 `done` 이벤트를 낼 때만 EDL 을 DB 에 쓴다(routes/editor/index.ts).
   */
  onTurnDone: (persisted: boolean) => void;
  onError: (msg: string) => void;
}) {
  const idRef = useRef(0);
  const nextId = () => `i${++idRef.current}`;
  // Saved turns are replayed once, at mount — thinking blocks and live tool
  // state aren't persisted, so a restored transcript is messages + edit cards.
  const [items, setItems] = useState<Item[]>(() => restoreItems(chat.history ?? [], nextId));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const itemsRef = useRef<Item[]>(items);
  itemsRef.current = items;

  // Pin to bottom as the transcript grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items]);

  // Auto-grow the composer up to a few lines.
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [input]);

  const send = useCallback(
    async (raw: string) => {
      const goal = raw.trim();
      if (!goal || busy) return;
      setInput('');

      const priorTurns: ChatTurn[] = itemsRef.current
        .filter(
          (i): i is Extract<Item, { kind: 'user' | 'assistant' }> =>
            i.kind === 'user' || i.kind === 'assistant'
        )
        .map((i) => ({ role: i.kind, content: i.text }));
      // Keep the newest turns: a long (restored) transcript would otherwise blow
      // past the server's per-request cap. Never open on a dangling reply.
      const turns: ChatTurn[] = [...priorTurns, { role: 'user' as const, content: goal }].slice(
        -MAX_CHAT_TURNS
      );
      if (turns[0]?.role === 'assistant') turns.shift();

      setItems((prev) => [...prev, { id: nextId(), kind: 'user', text: goal }]);
      setBusy(true);
      onBusyChange(true);
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      // Per-turn cursors into the transcript.
      let thinkId: string | null = null;
      let asstId: string | null = null;
      let editId: string | null = null;
      let editCount = 0;
      /*
       * 🔴 서버는 `done` 을 낼 때 **비로소** EDL 을 저장한다. 그 전에 끊기면
       * (500 · 중지 · 연결 끊김) 서버에는 아무것도 안 들어간다. 예전에는
       * `finally` 에서 무조건 onTurnDone() 을 불러 «저장됨» 으로 표시했고,
       * 그러면 AI 가 적용한 편집도, 그 전에 손으로 한 편집도 함께 «저장됨» 이
       * 되어 저장 버튼이 잠기고 탭 닫기 경고도 안 떴다 — 그대로 유실이다
       * (2026-09-11 점검 H1).
       */
      let persisted = false;
      /** onEvent 호출 시점에 직접 센 편집 수(위 주석 참조). */
      let applied = 0;
      const startedAt = Date.now();

      const onEvent = (ev: AgentEvent) => {
        setItems((prev) => {
          const next = [...prev];
          const patch = (id: string, fn: (it: Item) => Item) => {
            const idx = next.findIndex((i) => i.id === id);
            if (idx >= 0) next[idx] = fn(next[idx]!);
          };
          switch (ev.type) {
            case 'thinking':
              if (!thinkId) {
                thinkId = nextId();
                next.push({
                  id: thinkId,
                  kind: 'thinking',
                  text: ev.text,
                  live: true,
                  seconds: null,
                });
              } else {
                patch(thinkId, (it) =>
                  it.kind === 'thinking'
                    ? { ...it, text: it.text ? `${it.text}\n${ev.text}` : ev.text, live: true }
                    : it
                );
              }
              break;
            case 'message':
              if (thinkId) {
                patch(thinkId, (it) =>
                  it.kind === 'thinking'
                    ? {
                        ...it,
                        live: false,
                        seconds: it.seconds ?? Math.round((Date.now() - startedAt) / 1000),
                      }
                    : it
                );
              }
              if (!asstId) {
                asstId = nextId();
                next.push({ id: asstId, kind: 'assistant', text: ev.text });
              } else {
                patch(asstId, (it) =>
                  it.kind === 'assistant' ? { ...it, text: it.text + ev.text } : it
                );
              }
              break;
            case 'edit':
              editCount += 1;
              if (!editId) {
                editId = nextId();
                next.push({
                  id: editId,
                  kind: 'tool',
                  icon: '✂️',
                  name: '타임라인 편집',
                  meta: `${editCount}개 적용`,
                  running: true,
                });
              } else {
                patch(editId, (it) =>
                  it.kind === 'tool' ? { ...it, meta: `${editCount}개 적용` } : it
                );
              }
              break;
            case 'tool':
              next.push({
                id: nextId(),
                kind: 'tool',
                icon: '🔍',
                name: 'GIF 검색',
                meta: ev.status,
                running: true,
              });
              break;
            case 'notice':
              next.push({ id: nextId(), kind: 'notice', text: ev.text });
              break;
            case 'error':
              next.push({ id: nextId(), kind: 'notice', text: ev.message });
              break;
            case 'done':
              break;
          }
          return next;
        });
        /*
         * Side effects outside the state updater.
         *
         * ⚠️ `persisted`·`applied` 를 위 updater 안에서 세지 않는다 — updater 는
         * 호출 시점이 아니라 **React 가 업데이트를 처리할 때** 실행돼서, 스트림이
         * 끝나고 `finally` 가 값을 읽는 순간까지 아직 안 돌았을 수 있다.
         * 그러면 성공한 턴을 «저장 안 됨» 으로 남기게 된다.
         */
        if (ev.type === 'edit') applied += 1;
        if (ev.type === 'done') persisted = true;
        if (ev.type === 'edit') onEdit(ev.command);
        if (ev.type === 'error') onError(ev.message);
      };

      try {
        await chat.send(turns, getEdl(), onEvent, ctrl.signal);
      } finally {
        setItems((prev) =>
          prev.map((it) =>
            it.kind === 'thinking'
              ? {
                  ...it,
                  live: false,
                  seconds: it.seconds ?? Math.round((Date.now() - startedAt) / 1000),
                }
              : it.kind === 'tool'
                ? { ...it, running: false }
                : it
          )
        );
        abortRef.current = null;
        setBusy(false);
        onBusyChange(false);
        // 끊긴 턴이 편집을 남겼으면 «직접 저장해야 한다»고 말해 준다.
        if (!persisted && applied > 0) {
          setItems((prev) => [
            ...prev,
            {
              id: nextId(),
              kind: 'notice',
              text: '편집은 타임라인에 남아 있지만 저장되지 않았어요. 저장을 눌러 주세요.',
            },
          ]);
        }
        onTurnDone(persisted);
      }
    },
    [busy, chat, getEdl, onBusyChange, onEdit, onError, onTurnDone]
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  // "새 대화": clear the panel AND the saved transcript, so the next turn starts
  // the agent with no prior context (and a reload doesn't bring it back).
  const startNewChat = useCallback(() => {
    setItems([]);
    if (!chat.clear) return;
    void chat.clear().catch(() => onError('대화 기록을 지우지 못했어요'));
  }, [chat, onError]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  const empty = items.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-background">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b px-4 py-3">
        <div
          className="flex h-6 w-6 place-items-center items-center justify-center rounded-[0.45rem] text-[0.8rem] font-bold text-[#160b06]"
          style={{ background: 'linear-gradient(145deg, hsl(var(--ai)), hsl(var(--ai)/0.55))' }}
        >
          ✦
        </div>
        <div className="leading-tight">
          <div className="text-[0.85rem] font-semibold tracking-tight">AI 에디터</div>
          <div className="text-[0.68rem] text-muted-foreground">하이라이트를 함께 편집해요</div>
        </div>
        <div className="flex-1" />
        {items.length > 0 && !busy && (
          <button
            type="button"
            title="새 대화"
            onClick={startNewChat}
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            ✎
          </button>
        )}
      </div>

      {/* Transcript */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3.5 py-4">
        {empty ? (
          // min-h-full (not h-full): centers when there's room, but grows and
          // scrolls when the panel is short (2-column layout shares height with
          // the timeline) instead of clipping the avatar/chips.
          <div className="flex min-h-full flex-col items-center justify-center px-3 py-2 text-center">
            <div
              className="mb-4 grid h-10 w-10 place-items-center rounded-xl text-xl text-[#160b06]"
              style={{ background: 'linear-gradient(145deg, hsl(var(--ai)), hsl(var(--ai)/0.55))' }}
            >
              ✦
            </div>
            <div className="text-[1.02rem] font-semibold tracking-tight">
              무엇을 만들어 드릴까요?
            </div>
            <p className="mt-2 max-w-[19rem] text-[0.79rem] leading-relaxed text-muted-foreground">
              길이·컷·화면 구성을 맡아드려요. 자막은 전사에서 자동으로 만들어집니다.
            </p>
            <div className="mt-6 flex max-w-[22rem] flex-wrap justify-center gap-1.5">
              {SAMPLE_REQUESTS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  title={s.prompt}
                  onClick={() => {
                    setInput(s.prompt);
                    taRef.current?.focus();
                  }}
                  className="rounded-full border bg-card px-3 py-1.5 text-[0.735rem] text-muted-foreground transition-colors hover:border-[hsl(var(--ai)/0.4)] hover:bg-secondary hover:text-foreground"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {items.map((it) => (
              <MessageRow key={it.id} item={it} />
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t px-3.5 pb-4 pt-3">
        <div className="flex flex-col gap-2 rounded-2xl border bg-card px-3 pb-2 pt-2.5 focus-within:border-[hsl(var(--ai)/0.55)] focus-within:shadow-[0_0_0_3px_hsl(var(--ai)/0.1)]">
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="편집을 요청하세요… (예: 15초 한 컷으로 잘라줘)"
            className="max-h-[140px] w-full resize-none bg-transparent text-[0.83rem] leading-relaxed outline-none placeholder:text-muted-foreground"
          />
          <div className="flex items-center gap-2">
            <span className="text-[0.68rem] text-muted-foreground">
              {busy ? 'AI가 편집 중…' : 'Enter 전송 · Shift+Enter 줄바꿈'}
            </span>
            {busy ? (
              <button
                type="button"
                onClick={stop}
                title="중지"
                className="ml-auto grid h-8 w-8 place-items-center rounded-lg bg-secondary text-foreground hover:bg-muted"
              >
                <span className="block h-2.5 w-2.5 rounded-[2px] bg-foreground" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void send(input)}
                disabled={!input.trim()}
                title="전송"
                className="ml-auto grid h-8 w-8 place-items-center rounded-lg bg-primary text-base text-primary-foreground disabled:opacity-35"
              >
                ↑
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageRow({ item }: { item: Item }) {
  if (item.kind === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[84%] whitespace-pre-wrap rounded-[1.05rem] rounded-br-sm bg-secondary px-3 py-2 text-[0.83rem] leading-relaxed">
          {item.text}
        </div>
      </div>
    );
  }
  if (item.kind === 'notice') {
    return <div className="pl-8 text-[0.75rem] text-muted-foreground">{item.text}</div>;
  }
  // assistant / thinking / tool all sit in the agent column (avatar + body).
  return (
    <div className="flex gap-2.5">
      <Avatar />
      <div className="min-w-0 flex-1">
        {item.kind === 'thinking' && <ThinkingBlock item={item} />}
        {item.kind === 'assistant' && (
          <div className="whitespace-pre-wrap text-[0.83rem] leading-relaxed">{item.text}</div>
        )}
        {item.kind === 'tool' && <ToolCard item={item} />}
      </div>
    </div>
  );
}

function ThinkingBlock({ item }: { item: Extract<Item, { kind: 'thinking' }> }) {
  return (
    <details open={item.live} className="group">
      <summary
        className={`flex cursor-pointer list-none items-center gap-1.5 py-0.5 text-[0.75rem] ${
          item.live ? 'text-[hsl(var(--ai))]' : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {item.live ? (
          <span className="inline-flex items-center gap-[3px]">
            <Dot /> <Dot d={0.2} /> <Dot d={0.4} />
          </span>
        ) : (
          <span className="text-[0.58rem] opacity-70 transition-transform group-open:rotate-90">
            ▶
          </span>
        )}
        {item.live ? '생각하는 중' : `${item.seconds ?? 0}초 동안 생각함`}
      </summary>
      <div className="mt-1.5 whitespace-pre-wrap border-l-2 border-[hsl(var(--ai)/0.3)] pl-3 text-[0.765rem] leading-relaxed text-muted-foreground">
        {item.text}
      </div>
    </details>
  );
}

function ToolCard({ item }: { item: Extract<Item, { kind: 'tool' }> }) {
  return (
    <div className="flex items-center gap-2.5 rounded-[0.6rem] border bg-card/60 px-2.5 py-2 text-[0.78rem]">
      <span className="grid h-[1.35rem] w-[1.35rem] flex-none place-items-center rounded-md bg-secondary text-[0.8rem]">
        {item.icon}
      </span>
      <div className="min-w-0">
        <div className="font-medium tracking-tight">{item.name}</div>
        {item.meta && (
          <div className="mt-px truncate text-[0.715rem] text-muted-foreground">{item.meta}</div>
        )}
      </div>
      <div className="ml-auto grid flex-none place-items-center">
        {item.running ? (
          <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[hsl(var(--ai)/0.25)] border-t-[hsl(var(--ai))]" />
        ) : (
          <span className="text-[0.9rem] text-emerald-700 dark:text-emerald-400">✓</span>
        )}
      </div>
    </div>
  );
}

function Dot({ d = 0 }: { d?: number }) {
  return (
    <span
      className="inline-block h-1 w-1 animate-pulse rounded-full bg-[hsl(var(--ai))]"
      style={{ animationDelay: `${d}s`, animationDuration: '1.2s' }}
    />
  );
}
